import React from 'react';
import { Link } from 'react-router-dom';
import { LinkIcon } from '@heroicons/react/24/outline';
import { MilestoneMap1 } from '@mm-shared/types/MilestoneMap1';
import { CollapseIcon } from '../../components_misc/CollapseIcon';
import { ExpandIcon } from '../../components_misc/ExpandIcon';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';

export interface MapTitleBarProps {
  milestoneMap: MilestoneMap1;
  collapseLevel: 'expanded' | 'titleOnly' | 'fullyCollapsed';
  isMainMap?: boolean;
  onCollapseClick: () => void;
  onExpandClick: () => void;

  // Layout props
  width: number;
  height: number;

  // Styling approach
  variant: 'react-component' | 'inline-styles';

  // Z-index management (for react-component variant)
  containerZIndex?: number;
  buttonZIndex?: number;
}

/**
 * MapTitleBar - Reusable title bar component for milestone maps
 *
 * Used in:
 * - MapNode: Actual map node title bars with full interactivity
 * - FullyCollapsedHoverPreview: Hover preview above thick lines
 *
 * Supports two styling variants:
 * - 'react-component': Uses Tailwind classes and React patterns (for React Flow rendering in MapNode)
 * - 'inline-styles': Uses inline styles for absolute positioning (for hover previews for rendering outside of React Flow)
 */
export function MapTitleBar({
  milestoneMap,
  collapseLevel,
  isMainMap = false,
  onCollapseClick,
  onExpandClick,
  width,
  height,
  variant,
  containerZIndex = 2,
  buttonZIndex = 40,
}: MapTitleBarProps) {
  const title = milestoneMap.title;

  // Determine button states and titles
  const collapseDisabled = collapseLevel === 'fullyCollapsed';
  const expandDisabled = collapseLevel === 'expanded';

  const collapseTitle = collapseDisabled
    ? 'Already fully collapsed'
    : isMainMap
      ? 'Collapse to title only'
      : collapseLevel === 'expanded'
        ? 'Collapse to title only'
        : 'Fully collapse';

  const expandTitle = expandDisabled
    ? 'Already fully expanded'
    : collapseLevel === 'titleOnly'
      ? 'Expand to full view'
      : 'Expand to title only';

  if (variant === 'react-component') {
    // React component variant (for MapNode)
    return (
      <div
        className="absolute grid grid-cols-[auto_1fr_auto] items-center bg-white font-bold border border-slate-400 px-4"
        style={{
          left: 0,
          width,
          height: height - 1,
          position: 'relative',
          zIndex: buttonZIndex, // Use buttonZIndex for the whole container to ensure it's above edges
        }}
      >
        {/* Left side: Link icon */}
        <div className="flex items-center justify-start mr-2">
          <Link
            to={`/${milestoneMap.owner?.name}/maps/${getShortIdFromUuid(milestoneMap.uuid)}`}
            className="flex items-center justify-center p-1 hover:bg-gray-100 rounded transition-colors"
            title={`Open ${title} in new tab`}
          >
            <LinkIcon className="w-4 h-4 text-slate-600" />
          </Link>
        </div>

        {/* Center: Title - expands to fill available space */}
        <div className="flex items-center justify-center min-w-0">
          <div className="truncate text-center" title={title}>
            {title}
          </div>
        </div>

        {/* Right side: Control buttons */}
        <div className="flex items-center justify-end gap-1 ml-2">
          {/* Collapse Button */}
          <button
            onClick={onCollapseClick}
            className={`flex items-center justify-center p-1 rounded transition-colors ${
              collapseDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
            }`}
            disabled={collapseDisabled}
            title={collapseTitle}
          >
            <CollapseIcon className="w-4 h-4 text-gray-600" />
          </button>

          {/* Expand Button */}
          <button
            onClick={onExpandClick}
            className={`flex items-center justify-center p-1 rounded transition-colors ${
              expandDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
            }`}
            disabled={expandDisabled}
            title={expandTitle}
          >
            <ExpandIcon className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>
    );
  } else {
    // Inline styles variant (for FullyCollapsedHoverPreview)
    return (
      <div
        style={{
          position: 'absolute',
          left: 0,
          width,
          height: height - 1,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          backgroundColor: 'white',
          fontWeight: 'bold',
          border: '1px solid rgb(229, 231, 235)',
          paddingLeft: '1rem',
          paddingRight: '1rem',
          zIndex: containerZIndex,
        }}
      >
        {/* Left side: Link icon */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginRight: '0.5rem',
          }}
        >
          <Link
            to={`/${milestoneMap.owner?.name}/maps/${getShortIdFromUuid(milestoneMap.uuid)}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.25rem',
              borderRadius: '0.25rem',
              transition: 'background-color 0.2s',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgb(243, 244, 246)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            title={`Open ${title} in new tab`}
          >
            <LinkIcon
              style={{
                width: '1rem',
                height: '1rem',
                color: 'rgb(75, 85, 99)',
              }}
            />
          </Link>
        </div>

        {/* Center: Title - expands to fill available space */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
            title={title}
          >
            {title}
          </div>
        </div>

        {/* Right side: Control buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.25rem',
            marginLeft: '0.5rem',
          }}
        >
          {/* Collapse Button */}
          {collapseDisabled ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.25rem',
                opacity: 0.5,
                cursor: 'not-allowed',
              }}
            >
              <CollapseIcon
                style={{
                  width: '1rem',
                  height: '1rem',
                  color: 'rgb(75, 85, 99)',
                }}
              />
            </div>
          ) : (
            <button
              onClick={onCollapseClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.25rem',
                borderRadius: '0.25rem',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgb(243, 244, 246)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              title={collapseTitle}
            >
              <CollapseIcon
                style={{
                  width: '1rem',
                  height: '1rem',
                  color: 'rgb(75, 85, 99)',
                }}
              />
            </button>
          )}

          {/* Expand Button */}
          {expandDisabled ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.25rem',
                opacity: 0.5,
                cursor: 'not-allowed',
              }}
            >
              <ExpandIcon
                style={{
                  width: '1rem',
                  height: '1rem',
                  color: 'rgb(75, 85, 99)',
                }}
              />
            </div>
          ) : (
            <button
              onClick={onExpandClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.25rem',
                borderRadius: '0.25rem',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgb(243, 244, 246)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              title={expandTitle}
            >
              <ExpandIcon
                style={{
                  width: '1rem',
                  height: '1rem',
                  color: 'rgb(75, 85, 99)',
                }}
              />
            </button>
          )}
        </div>
      </div>
    );
  }
}
